/* eslint-disable react/prop-types */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Globe, Mail, MapPin, Phone } from 'lucide-react';
import { sanitizeRouteParam } from '@/utils';
import { triggerDownload } from '@/utils/download';
import { getApiErrorMessage } from '@/utils/ValidationErrors';
import { FileBrowser } from '@/components/files';
import portalService from '@/services/portal/portalService';
import { statusLabel } from './matterStatus';

const fmtDate = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : null;

/** Whether any node in the tree is View Only, to explain the missing download buttons. */
const hasViewOnly = (nodes) => nodes.some((node) => node.can_download === false || hasViewOnly(node.children ?? []));

const Detail = ({ label, value }) =>
  value ? (
    <div className="flex justify-between gap-4 py-1.5 text-2sm border-b border-gray-100 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 text-right">{value}</span>
    </div>
  ) : null;

/**
 * One matter as a guest sees it (OL-195): its basic details, the firm's
 * contact details, and the files shared with them. Nothing else of the matter
 * reaches this page — the server sends only the agreed fields.
 */
const PortalMatter = () => {
  const { matterId: rawId } = useParams();
  const matterId = sanitizeRouteParam(rawId);
  const [matter, setMatter] = useState(null);
  const [viewOnlyPresent, setViewOnlyPresent] = useState(false);
  const [notFound, setNotFound] = useState(null);
  const [zipping, setZipping] = useState(false);

  /* Memoized — FileBrowser reloads whenever this identity changes. */
  const api = useMemo(
    () => ({
      // One request brings the details and the tree.
      getTree: async () => {
        try {
          const payload = await portalService.getMatter(matterId);
          setMatter(payload.matter);
          setViewOnlyPresent(hasViewOnly(payload.nodes ?? []));
          return payload.nodes ?? [];
        } catch (error) {
          // The page explains a 404 itself; rethrowing would add FileBrowser's error toast.
          if (error?.response?.status === 404) {
            setNotFound(getApiErrorMessage(error, 'That matter is not available.'));
            return [];
          }
          throw error;
        }
      },
      getPreview: (fileId) => portalService.getPreview(matterId, fileId),
      getPdf: (fileId) => portalService.getPdf(matterId, fileId),
      getDownloadUrl: (fileId) => portalService.getDownloadUrl(matterId, fileId),
    }),
    [matterId],
  );

  const downloadFolder = async (node) => {
    if (zipping) return;
    setZipping(true);
    try {
      triggerDownload(await portalService.downloadFolderZip(matterId, node.id), `${node.name}.zip`);
    } catch (error) {
      if (error?.response?.status !== 401) {
        toast.error('Download failed.', { description: getApiErrorMessage(error, 'Please try again.') });
      }
    } finally {
      setZipping(false);
    }
  };

  if (notFound) {
    return (
      <div className="card">
        <div className="card-body flex flex-col items-center gap-3 py-14 text-center">
          <h3 className="text-base font-medium text-gray-900">This matter is not available</h3>
          <p className="text-2sm text-gray-600">The firm may have stopped sharing it with you.</p>
          <Link to="/portal" className="btn btn-light">Back to My Matters</Link>
        </div>
      </div>
    );
  }

  const firm = matter?.firm;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/portal" className="btn btn-sm btn-light"><ArrowLeft size={14} /> My Matters</Link>
        {matter && (
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-xl font-semibold text-gray-900 truncate">{matter.name}</h1>
            <span className="badge badge-sm badge-outline">{statusLabel(matter.status)}</span>
          </div>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-3 items-start">
        <div className="card lg:col-span-2">
          <div className="card-header">
            <h3 className="card-title">Shared documents</h3>
          </div>
          <div className="card-body">
            <FileBrowser
              fileableType="matter"
              fileableId={matterId}
              readOnly
              api={api}
              onDownloadFolder={downloadFolder}
              downloadFolderBusy={zipping}
            />
            <p className="text-2xs text-gray-500 mt-3">
              {viewOnlyPresent
                ? 'Some documents are view only: they open here but can’t be downloaded. '
                : ''}
              Every document you open or download is recorded.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-5">
          {matter && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Matter details</h3>
              </div>
              <div className="card-body pt-2">
                <Detail label="Matter number" value={matter.number} />
                <Detail label="Matter type" value={matter.matter_type} />
                <Detail label="Status" value={statusLabel(matter.status)} />
                <Detail label="Opened" value={fmtDate(matter.open_date)} />
                <Detail label="Closed" value={fmtDate(matter.closed_date)} />
                {matter.team?.length > 0 && (
                  <div className="pt-3">
                    <span className="text-2xs uppercase tracking-wide text-gray-500">Your team</span>
                    {matter.team.map((member) => (
                      <div key={member.email} className="flex flex-col py-1.5 text-2sm">
                        <span className="text-gray-900">{member.name}</span>
                        <a href={`mailto:${member.email}`} className="text-2xs link">{member.email}</a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {firm && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title flex items-center gap-2"><Building2 size={16} /> {firm.name}</h3>
              </div>
              <div className="card-body flex flex-col gap-2 pt-3 text-2sm text-gray-700">
                {firm.phone && <a href={`tel:${firm.phone}`} className="flex items-center gap-2 hover:text-primary"><Phone size={14} /> {firm.phone}</a>}
                {firm.email && <a href={`mailto:${firm.email}`} className="flex items-center gap-2 hover:text-primary"><Mail size={14} /> {firm.email}</a>}
                {firm.website && (
                  <a href={firm.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-primary">
                    <Globe size={14} /> {firm.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
                {firm.address?.length > 0 && (
                  <span className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 shrink-0" /> <span>{firm.address.map((line) => <span key={line} className="block">{line}</span>)}</span></span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { PortalMatter };
